import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Company, User, Employee, CompanyUser } from '../types';

interface TenantContextType {
  currentCompany: Company | null;
  companies: Company[];
  currentUser: User | null;
  currentEmployee: Employee | null;
  currentCompanyUser: CompanyUser | null;
  setCurrentCompany: (company: Company | null) => void;
  loadCompanies: () => Promise<void>;
  login: (companyRut: string, email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateOnboardingStatus: (onboardingType: 'Onboarding1' | 'Onboarding2' | 'Onboarding3', completed: boolean) => Promise<void>;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const [currentCompanyUser, setCurrentCompanyUser] = useState<CompanyUser | null>(null);
  const [allCompanyUsers, setAllCompanyUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Flag para evitar recargas cuando solo se refresca el token
  const isInitialLoadRef = useRef<boolean>(true);
  // Ref para acceder al valor actual de currentCompany dentro del callback de onAuthStateChange
  const currentCompanyRef = useRef<Company | null>(null);

  const loadCompanies = async (forceLoading: boolean = false) => {
    try {
      // Solo poner isLoading en true si:
      // 1. Se fuerza explícitamente (forceLoading = true), O
      // 2. No hay empresa actual cargada
      // Esto evita desmontar DataProvider cuando ya hay datos cargados
      if (forceLoading || !currentCompany) {
        setIsLoading(true);
      }
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCurrentUser(null);
        setCurrentCompany(null);
        setCompanies([]);
        return;
      }

      setCurrentUser(user as User);

      // Get user's companies through company_users relationship
      // Primero obtener los company_users
      const { data: companyUsers, error: companyUsersError } = await supabase
        .from('company_users')
        .select(`
          id,
          company_id,
          user_id,
          Onboarding1,
          Onboarding2,
          Onboarding3,
          role
        `)
        .eq('user_id', user.id);

      if (companyUsersError) {
        console.error('Error loading user companies:', companyUsersError);
        return;
      }

      if (!companyUsers || companyUsers.length === 0) {
        setCompanies([]);
        setCurrentCompany(null);
        return;
      }

      // Luego obtener las companies con todos los campos necesarios
      // Usar select('*') para obtener todos los campos disponibles y evitar errores 400
      const companyIds = companyUsers.map(cu => cu.company_id);
      
      // Si solo hay un ID, usar eq en lugar de in para evitar problemas
      let companiesData, companiesError;
      if (companyIds.length === 1) {
        const result = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyIds[0]);
        companiesData = result.data;
        companiesError = result.error;
      } else {
        const result = await supabase
          .from('companies')
          .select('*')
          .in('id', companyIds);
        companiesData = result.data;
        companiesError = result.error;
      }

      if (companiesError) {
        console.error('Error loading companies:', companiesError);
        // Si hay error, intentar con una consulta más simple sin los campos nuevos
        console.log('Attempting fallback query without new fields...');
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('companies')
          .select('id, name, rut, email, phone, address, legal_representative, legal_representative_rut, technical_contact, technical_email, contact_phone, plan_id, created_at, updated_at')
          .in('id', companyIds);
        
        if (fallbackError) {
          console.error('Fallback query also failed:', fallbackError);
          return;
        }
        
        // Usar datos del fallback y establecer valores por defecto para campos faltantes
        if (fallbackData) {
          const companiesWithDefaults = fallbackData.map((c: any) => ({
            ...c,
            accept: false,
            business_line: 10,
            business_word: 10,
            islite: false
          }));
          companiesData = companiesWithDefaults;
        } else {
          return;
        }
      }

      // Combinar los datos de company_users con companies
      const userCompanies: Company[] = [];
      const allCompanyUsersData: CompanyUser[] = [];
      
      if (companiesData && companiesData.length > 0) {
        for (const cu of companyUsers) {
          const company = companiesData.find(c => c.id === cu.company_id);
          if (company) {
            userCompanies.push(company as unknown as Company);
            
            // Store all company users
            allCompanyUsersData.push({
              id: cu.id,
              company_id: cu.company_id,
              user_id: cu.user_id,
              Onboarding1: cu.Onboarding1 || false,
              Onboarding2: cu.Onboarding2 || false,
              Onboarding3: cu.Onboarding3 || false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          }
        }
      }
      
      setCompanies(userCompanies);
      setAllCompanyUsers(allCompanyUsersData);
      
      // Set current company user based on saved company
      const savedCompanyId = localStorage.getItem('currentCompanyId');
      const currentCompanyUserData = savedCompanyId 
        ? allCompanyUsersData.find(cu => cu.company_id === savedCompanyId)
        : allCompanyUsersData[0] || null;
      setCurrentCompanyUser(currentCompanyUserData || null);

      // Set current company (first one or previously selected)
      if (userCompanies.length > 0) {
        const savedCompanyId = localStorage.getItem('currentCompanyId');
        const savedCompany = savedCompanyId 
          ? userCompanies.find(c => c.id === savedCompanyId)
          : null;
        
        const newCompany = savedCompany || userCompanies[0];
        
        // Solo actualizar currentCompany si el ID realmente cambió
        // Esto evita recargas innecesarias cuando la empresa es la misma
        setCurrentCompany(prevCompany => {
          if (prevCompany?.id === newCompany.id) {
            // Si es la misma empresa, mantener la referencia anterior para evitar re-renders
            currentCompanyRef.current = prevCompany;
            return prevCompany;
          }
          currentCompanyRef.current = newCompany;
          return newCompany;
        });
        
        // Load employee data for the current company
        if (savedCompany || userCompanies[0]) {
          const companyId = (savedCompany || userCompanies[0]).id;
          const { data: employeeData, error: employeeError } = await supabase
            .from('employees')
            .select('*')
            .eq('company_id', companyId)
            .eq('email', user.email)
            .maybeSingle();
            
          if (employeeError) {
            console.error('Error loading employee data:', employeeError);
          } else if (employeeData) {
            setCurrentEmployee(employeeData as Employee);
          } else {
            console.log('No employee data found for user:', user.email);
          }
        }
      }

    } catch (error) {
      console.error('Error in loadCompanies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetCurrentCompany = (company: Company | null) => {
    setCurrentCompany(company);
    if (company) {
      localStorage.setItem('currentCompanyId', company.id);
      // Update current company user
      const newCompanyUser = allCompanyUsers.find(cu => cu.company_id === company.id);
      setCurrentCompanyUser(newCompanyUser || null);
    } else {
      localStorage.removeItem('currentCompanyId');
      setCurrentCompanyUser(null);
    }
  };

  const login = async (companyRut: string, email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // First, authenticate the user
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        console.error('Authentication error:', authError);
        
        // Provide more specific error messages
        if (authError.message.includes('Invalid login credentials')) {
          throw new Error('Credenciales inválidas. Verifique su email y contraseña.');
        } else if (authError.message.includes('Email not confirmed')) {
          throw new Error('Email no confirmado. Revise su bandeja de entrada.');
        } else if (authError.message.includes('Too many requests')) {
          throw new Error('Demasiados intentos. Espere unos minutos antes de intentar nuevamente.');
        } else if (authError.message.includes('timeout') || authError.message.includes('TIMEOUT')) {
          throw new Error('Error de conexión. Verifique su conexión a internet e intente nuevamente.');
        }
        
        throw new Error(authError.message);
      }

      if (!authData.user) {
        throw new Error('Error de autenticación. Usuario no encontrado.');
      }

      // Check if the user is associated with the company with the provided RUT
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('id, name, rut')
        .eq('rut', companyRut)
        .single();

      if (companyError || !companyData) {
        console.error('Company not found:', companyError);
        await supabase.auth.signOut();
        throw new Error(`Empresa con RUT ${companyRut} no encontrada. Verifique el RUT ingresado.`);
      }

      // Check if the user is associated with this company
      const { data: userCompanyData, error: userCompanyError } = await supabase
        .from('company_users')
        .select('id, role')
        .eq('user_id', authData.user.id)
        .eq('company_id', companyData.id)
        .single();

      if (userCompanyError || !userCompanyData) {
        console.error('User not associated with company:', userCompanyError);
        await supabase.auth.signOut();
        throw new Error(`El usuario ${email} no está asociado con la empresa ${companyData.name}. Contacte al administrador.`);
      }

      // If everything is valid, load companies and set current user
      await loadCompanies();
      return true;

    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error types
      if (error instanceof Error) {
        // Check for network/timeout errors
        if (error.message.includes('timeout') || 
            error.message.includes('TIMEOUT') || 
            error.message.includes('ERR_CONNECTION_TIMED_OUT') ||
            error.message.includes('Failed to fetch')) {
          throw new Error('Error de conexión. Verifique su conexión a internet e intente nuevamente.');
        }
        
        // Re-throw the error to be handled by the component
        throw error;
      }
      
      throw new Error('Error inesperado durante el inicio de sesión. Intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateOnboardingStatus = async (onboardingType: 'Onboarding1' | 'Onboarding2' | 'Onboarding3', completed: boolean) => {
    if (!currentCompanyUser) {
      console.error('No current company user found');
      return;
    }

    console.log(`🔄 Updating ${onboardingType} to ${completed} for user:`, currentCompanyUser.id);

    try {
      const { error } = await supabase
        .from('company_users')
        .update({ [onboardingType]: completed })
        .eq('id', currentCompanyUser.id);

      if (error) {
        console.error('Error updating onboarding status:', error);
        throw error;
      }

      // Update local state
      setCurrentCompanyUser(prev => prev ? { ...prev, [onboardingType]: completed } : null);
      
      console.log(`✅ Onboarding ${onboardingType} status updated to:`, completed);
      console.log('📊 Current company user state:', currentCompanyUser);
    } catch (error) {
      console.error('Error updating onboarding status:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('Iniciando logout...');
      
      // Clear local state first
      setCurrentUser(null);
      setCurrentCompany(null);
      setCurrentEmployee(null);
      setCurrentCompanyUser(null);
      setCompanies([]);
      localStorage.removeItem('currentCompanyId');
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Error during logout:', error);
        // Don't throw here - we want to clear local session even if server call fails
      }
      
      console.log('Logout completado');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, we clear the local session
      setCurrentUser(null);
      setCurrentCompany(null);
      setCurrentEmployee(null);
      setCurrentCompanyUser(null);
      setCompanies([]);
      localStorage.removeItem('currentCompanyId');
    }
  };

  useEffect(() => {
    // Primera carga - forzar loading
    loadCompanies(true);
    isInitialLoadRef.current = false;

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      console.log('Auth state change:', event);
      
      // Solo recargar datos cuando el usuario inicia sesión Y es la primera carga
      // SIGNED_IN puede dispararse múltiples veces (incluso al cambiar de pestaña),
      // así que solo recargamos si es la carga inicial
      if (event === 'SIGNED_IN') {
        // Usar el ref para obtener el valor actual de currentCompany
        const hasCompany = currentCompanyRef.current !== null;
        
        // Solo recargar si es la carga inicial (isInitialLoadRef.current === true)
        // o si no hay empresa cargada (después de logout)
        if (isInitialLoadRef.current || !hasCompany) {
          console.log('SIGNED_IN: Carga inicial o sin empresa, recargando datos');
          isInitialLoadRef.current = false;
          loadCompanies(true);
        } else {
          console.log('SIGNED_IN: Ya hay empresa cargada, no se recargan datos para evitar recargas innecesarias');
        }
      } else if (event === 'TOKEN_REFRESHED') {
        // Solo verificar que el usuario sigue autenticado, sin recargar todos los datos
        // Esto evita recargas cuando se cambia de pestaña o se recupera el foco
        // Marcar que ya pasó la carga inicial para evitar efectos secundarios
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
        console.log('Token refrescado - no se recargan datos para evitar recargas innecesarias');
      } else if (event === 'SIGNED_OUT') {
        console.log('Usuario desconectado - limpiando estado');
        isInitialLoadRef.current = true;
        setCurrentUser(null);
        setCurrentCompany(null);
        setCurrentEmployee(null);
        setCompanies([]);
        localStorage.removeItem('currentCompanyId');
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: TenantContextType = {
    currentCompany,
    companies,
    currentUser,
    currentEmployee,
    currentCompanyUser,
    setCurrentCompany: handleSetCurrentCompany,
    loadCompanies,
    login,
    logout,
    updateOnboardingStatus,
    isLoading
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};